import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { tr } from "date-fns/locale";
import { Loader2, Mail, Send, CheckCircle2, AlertTriangle, RefreshCw, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getNotificationStatus, sendTestEmail } from "@/lib/adminApi";

const PROVIDER_LABEL = { resend: "Resend", smtp: "Gmail / SMTP", none: "Yapılandırılmadı" };
const STATUS_STYLE = {
  sent: "text-emerald-400",
  failed: "text-red-400",
  skipped: "text-amber-400",
};
const STATUS_LABEL = { sent: "Gönderildi", failed: "Hata", skipped: "Atlandı" };

export default function NotificationsPanel() {
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({ queryKey: ["admin-notify"], queryFn: getNotificationStatus });
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);

  const fmt = (iso) => { try { return format(parseISO(iso), "d MMM HH:mm", { locale: tr }); } catch { return iso; } };

  const test = async () => {
    setSending(true);
    try {
      const res = await sendTestEmail(to.trim());
      if (res.status === "sent") toast.success(`Test e-postası gönderildi: ${res.to}`);
      else if (res.status === "skipped") toast.warning(res.error || "Sağlayıcı yapılandırılmadı");
      else toast.error(res.error || "Gönderim başarısız");
      qc.invalidateQueries({ queryKey: ["admin-notify"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gönderim başarısız");
    } finally {
      setSending(false);
    }
  };

  if (isLoading || !data) {
    return <div className="py-20 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8A24A]" /></div>;
  }

  const configured = data.configured;

  return (
    <div className="space-y-8" data-testid="notifications-panel">
      <div className="grid md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-white/10 bg-[#141517] p-5" data-testid="notify-provider-card">
          <div className="text-xs uppercase tracking-wide text-[#9A9387]">Sağlayıcı</div>
          <div className="flex items-center gap-2 mt-2 text-lg text-[#F7F2E8]">
            {configured ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <AlertTriangle className="w-5 h-5 text-amber-400" />}
            {PROVIDER_LABEL[data.provider] || data.provider}
          </div>
          <div className="text-xs text-[#9A9387] mt-1">{configured ? "E-postalar gerçek olarak gönderiliyor" : "Bildirimler kaydediliyor ama e-posta gönderilmiyor"}</div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#141517] p-5">
          <div className="text-xs uppercase tracking-wide text-[#9A9387]">Alıcı</div>
          <div className="mt-2 text-[#F7F2E8] break-all" data-testid="notify-recipient">{data.recipient || "—"}</div>
          <div className="text-xs text-[#9A9387] mt-1">Gönderen: {data.sender || "—"}</div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#141517] p-5">
          <div className="text-xs uppercase tracking-wide text-[#9A9387]">İstatistik</div>
          <div className="flex gap-4 mt-2 text-sm">
            <span className="text-emerald-400">{data.counts?.sent ?? 0} gönderildi</span>
            <span className="text-amber-400">{data.counts?.skipped ?? 0} atlandı</span>
            <span className="text-red-400">{data.counts?.failed ?? 0} hata</span>
          </div>
        </div>
      </div>

      {!configured && (
        <div className="rounded-2xl border border-[#C8A24A]/30 bg-[#C8A24A]/10 p-5 text-sm text-[#EDE7DA]" data-testid="notify-setup-help">
          <div className="flex items-center gap-2 font-medium"><Info className="w-4 h-4 text-[#D7B86A]" /> Kurulum: `backend/.env` dosyasına anahtarınızı ekleyip backend'i yeniden başlatın.</div>
          <div className="grid md:grid-cols-2 gap-4 mt-3">
            <div className="rounded-xl bg-[#0E0F10] border border-white/10 p-4">
              <div className="text-[#D7B86A] font-medium">Seçenek A — Resend (önerilen)</div>
              <pre className="text-xs text-[#B8B1A5] mt-2 whitespace-pre-wrap">{`RESEND_API_KEY="re_..."
SENDER_EMAIL="bildirim@alanadiniz.com"  # veya onboarding@resend.dev (test)
NOTIFY_EMAIL="sizin@eposta.com"`}</pre>
            </div>
            <div className="rounded-xl bg-[#0E0F10] border border-white/10 p-4">
              <div className="text-[#D7B86A] font-medium">Seçenek B — Gmail SMTP</div>
              <pre className="text-xs text-[#B8B1A5] mt-2 whitespace-pre-wrap">{`SMTP_USER="hesabiniz@gmail.com"
SMTP_PASS="uygulama-sifresi"  # Google Hesabı > Güvenlik > Uygulama şifreleri
NOTIFY_EMAIL="sizin@eposta.com"`}</pre>
            </div>
          </div>
          <div className="text-xs text-[#9A9387] mt-3">EMAIL_PROVIDER="auto" ile Resend anahtarı varsa Resend, yoksa SMTP bilgileri varsa Gmail kullanılır.</div>
        </div>
      )}

      <section className="rounded-2xl border border-white/10 bg-[#141517] p-5">
        <h2 className="flex items-center gap-2 font-display text-2xl text-[#F7F2E8]"><Mail className="w-5 h-5 text-[#C8A24A]" /> Test e-postası gönder</h2>
        <p className="text-xs text-[#9A9387] mt-1">Örnek bir teklif talebi bildirimi gönderir. Boş bırakırsanız varsayılan alıcıya gider.</p>
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <Input type="email" placeholder={data.recipient || "ornek@eposta.com"} value={to} onChange={(e) => setTo(e.target.value)}
            className="bg-[#0E0F10] border-white/15 text-[#EDE7DA] sm:max-w-sm" data-testid="test-email-input" />
          <Button onClick={test} disabled={sending} className="bg-[#C8A24A] text-[#141517] hover:bg-[#D7B86A] font-semibold" data-testid="test-email-button">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Gönder
          </Button>
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl text-[#F7F2E8]">Son bildirimler</h2>
          <Button variant="outline" size="sm" onClick={() => refetch()} className="border-white/15 text-[#EDE7DA] bg-transparent hover:bg-white/5">
            <RefreshCw className="w-4 h-4 mr-2" /> Yenile
          </Button>
        </div>
        {(!data.recent || data.recent.length === 0) ? (
          <div className="py-12 text-center text-[#9A9387]" data-testid="notify-empty">Henüz bildirim kaydı yok. Yeni bir teklif talebi geldiğinde burada görünür.</div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-white/10" data-testid="notify-table">
            <table className="w-full text-sm">
              <thead className="bg-[#141517] text-[#9A9387] text-left">
                <tr>
                  <th className="p-3 font-medium">Tarih</th>
                  <th className="p-3 font-medium">Tür</th>
                  <th className="p-3 font-medium">Konu</th>
                  <th className="p-3 font-medium">Alıcı</th>
                  <th className="p-3 font-medium">Durum</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/8">
                {data.recent.map((n) => (
                  <tr key={n.id} className="bg-[#0E0F10] hover:bg-[#141517] text-[#EDE7DA]">
                    <td className="p-3 whitespace-nowrap text-[#9A9387]">{fmt(n.created_at)}</td>
                    <td className="p-3">{n.kind === "test" ? "Test" : "Teklif"}</td>
                    <td className="p-3 max-w-[320px] truncate" title={n.subject}>{n.subject}</td>
                    <td className="p-3 text-[#B8B1A5]">{n.to}</td>
                    <td className={`p-3 ${STATUS_STYLE[n.status] || ""}`} title={n.error || ""}>{STATUS_LABEL[n.status] || n.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
